import React, { useState, useCallback, useRef, useMemo } from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ModuleRegistry } from '@ag-grid-community/core';
import type { ColDef, GridReadyEvent, SelectionChangedEvent, CellClickedEvent, GridApi, RowSelectionOptions } from '@ag-grid-community/core';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-alpine.css';
import type { ProductSummary, ReviewStatus } from '../types/product';
import SeoScoreCell from './SeoScoreCell';
import SellingBadge from './SellingBadge';

const REVIEW_BADGE: Record<string, { bg: string; color: string; label: string }> = {
  approved: { bg: '#dcfce7', color: '#15803d', label: 'Approved' },
  changes_requested: { bg: '#fef3c7', color: '#92400e', label: 'Changes' },
  denied: { bg: '#fee2e2', color: '#991b1b', label: 'Denied' },
  pending: { bg: '#dbeafe', color: '#1e40af', label: 'Pending' },
};

function ReviewCell({ value }: { value: ReviewStatus }) {
  if (!value) return <span style={{ color: '#d1d5db', fontSize: 11 }}>--</span>;
  const s = REVIEW_BADGE[value] || REVIEW_BADGE.pending;
  return <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }}>{s.label}</span>;
}

ModuleRegistry.registerModules([ClientSideRowModelModule]);

interface Props {
  rowData: ProductSummary[];
  loading: boolean;
  onProductClick: (cprcode: string) => void;
  onSelectionChanged: (cprcodes: string[]) => void;
}

function TranslationCell({ value }: { value: ProductSummary['has_translations'] }) {
  if (!value) return null;
  const langs = [
    { code: 'th', flag: 'TH' },
    { code: 'fr', flag: 'FR' },
    { code: 'de', flag: 'DE' },
    { code: 'zh', flag: 'ZH' },
    { code: 'ru', flag: 'RU' },
  ] as const;

  return (
    <div style={{ display: 'flex', gap: 3 }}>
      {langs.map((l) => (
        <span key={l.code} style={{
          display: 'inline-block',
          width: 22,
          height: 18,
          lineHeight: '18px',
          textAlign: 'center',
          borderRadius: 3,
          fontSize: 9,
          fontWeight: 600,
          background: value[l.code] ? '#dcfce7' : '#f3f4f6',
          color: value[l.code] ? '#15803d' : '#9ca3af',
        }}>
          {l.flag}
        </span>
      ))}
    </div>
  );
}

export default function ProductGrid({ rowData, loading, onProductClick, onSelectionChanged }: Props) {
  const gridRef = useRef<AgGridReact>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [quickFilter, setQuickFilter] = useState('');

  const columnDefs = useMemo<ColDef<ProductSummary>[]>(() => [
    {
      field: 'cprcode',
      headerName: 'CPR Code',
      width: 110,
      pinned: 'left',
    },
    {
      field: 'display_name',
      headerName: 'Product Name',
      flex: 2,
      minWidth: 250,
      cellStyle: { cursor: 'pointer', color: '#2563eb' },
    },
    {
      field: 'brand',
      headerName: 'Brand',
      width: 160,
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 160,
    },
    {
      field: 'seo_score',
      headerName: 'SEO Score',
      width: 110,
      cellRenderer: (params: { value: number }) => <SeoScoreCell value={params.value} />,
      sort: 'desc',
    },
    {
      field: 'is_selling',
      headerName: 'Status',
      width: 120,
      cellRenderer: (params: { value: boolean }) => <SellingBadge value={params.value} />,
    },
    {
      field: 'has_translations',
      headerName: 'Translations',
      width: 140,
      cellRenderer: (params: { value: ProductSummary['has_translations'] }) => <TranslationCell value={params.value} />,
      sortable: false,
    },
    {
      field: 'review_status',
      headerName: 'Review',
      width: 110,
      cellRenderer: (params: { value: ReviewStatus }) => <ReviewCell value={params.value} />,
    },
    {
      field: 'written_at',
      headerName: 'Written',
      width: 110,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleDateString();
      },
    },
  ], []);

  const defaultColDef = useMemo<ColDef>(() => ({
    sortable: true,
    resizable: true,
    suppressHeaderMenuButton: false,
  }), []);

  const rowSelection = useMemo<RowSelectionOptions>(() => ({
    mode: 'multiRow',
    checkboxes: true,
    headerCheckbox: true,
  }), []);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
  }, []);

  const onSelectionChange = useCallback((event: SelectionChangedEvent) => {
    const selected = event.api.getSelectedRows() as ProductSummary[];
    onSelectionChanged(selected.map((r) => r.cprcode));
  }, [onSelectionChanged]);

  const onCellClicked = useCallback((event: CellClickedEvent) => {
    if (event.colDef.field === 'display_name') {
      onProductClick(event.data.cprcode);
    }
  }, [onProductClick]);

  const onQuickFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuickFilter(val);
    gridApi?.setGridOption('quickFilterText', val);
  }, [gridApi]);

  const handleExport = useCallback(() => {
    gridApi?.exportDataAsCsv({
      fileName: 'villa-products-export.csv',
      columnKeys: ['cprcode', 'display_name', 'brand', 'category', 'seo_score', 'is_selling', 'written_at'],
    });
  }, [gridApi]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
        <input
          type="text"
          placeholder="Quick filter (search all columns)..."
          value={quickFilter}
          onChange={onQuickFilterChange}
          style={{ flex: 1, maxWidth: 400, padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
        />
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 13, color: '#6b7280' }}>
          {loading ? 'Loading...' : `${rowData.length.toLocaleString()} products`}
        </span>
        <button onClick={handleExport}
          style={{ padding: '6px 12px', background: 'white', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}>
          Export CSV
        </button>
      </div>

      {/* Grid */}
      <div className="ag-theme-alpine" style={{ flex: 1, minHeight: 400 }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          onSelectionChanged={onSelectionChange}
          onCellClicked={onCellClicked}
          rowSelection={rowSelection}
          animateRows
          getRowId={(params) => params.data.cprcode}
          loading={loading}
          enableCellTextSelection
        />
      </div>
    </div>
  );
}
